import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Eye, EyeOff, ChevronLeft, AlertCircle } from 'lucide-react';
import { extractErrorMessage } from '@/lib/api-errors';

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    birth_date: '',
    marketing_opt_in: false
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!ageConfirmed) {
      toast.error('Deve confirmar que tem 18 anos ou mais');
      return;
    }

    // Validate age
    const birthDate = new Date(formData.birth_date);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    if (age < 18) {
      toast.error('Deve ter 18 anos ou mais para se registar');
      return;
    }

    setLoading(true);

    try {
      await register(formData);
      toast.success('Conta criada com sucesso!');
      navigate('/home');
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Erro ao criar conta'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1518893063132-36e46dbe2428?w=1920)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-background" />
        <div className="absolute inset-0 bg-black/40" />
        
        <div className="relative z-10 flex flex-col justify-end p-12">
          <h2 className="font-brand text-4xl font-bold text-foreground mb-4">
            Junta-te a nós
          </h2>
          <p className="text-muted-foreground text-lg max-w-md">
            Cria tua conta e tenha acesso a milhares de contos eróticos exclusivos.
          </p>
        </div>
      </div>

      {/* Right - Form */}
      <div className="w-full lg:w-1/2 flex items-start lg:items-center justify-center p-6 sm:p-8 pt-10 lg:pt-8">
        <div className="w-full max-w-md">
          {/* Back Button */}
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="mb-4 sm:mb-8 text-muted-foreground hover:text-white"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>

          {/* Logo */}
          <Link to="/" className="block mb-8">
            <img src="/logo-red.png" alt="Intimate.pt" className="h-10 w-auto" />
          </Link>

          <h2 className="text-2xl font-semibold text-foreground mb-2">Criar Conta</h2>
          <p className="text-muted-foreground mb-8">
            Já tens uma conta?{' '}
            <Link to="/login" className="text-ruby hover:underline">
              Entrar
            </Link>
          </p>

          {/* Age Warning */}
          <div className="bg-ruby/10 border border-ruby/30 rounded-lg p-4 mb-6 flex gap-3">
            <AlertCircle className="w-5 h-5 text-ruby flex-shrink-0 mt-0.5" />
            <p className="text-sm text-muted-foreground">
              Este site contém conteúdo adulto. Deve ter <strong>18 anos ou mais</strong> para criar uma conta.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name">Nome</Label>
              <Input
                id="name"
                type="text"
                placeholder="O seu nome"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                data-testid="register-name"
                className="bg-surface border-border h-12"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="seu@email.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                data-testid="register-email"
                className="bg-surface border-border h-12"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="birth_date">Data de Nascimento</Label>
              <Input
                id="birth_date"
                type="date"
                value={formData.birth_date}
                onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })}
                data-testid="register-birthdate"
                className="bg-surface border-border h-12"
                max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Palavra-passe</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  data-testid="register-password"
                  className="bg-surface border-border h-12 pr-12"
                  minLength={6}
                  required
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Checkbox 
                id="age" 
                checked={ageConfirmed}
                onCheckedChange={setAgeConfirmed}
                data-testid="register-age-confirm"
                className="mt-1"
              />
              <Label htmlFor="age" className="text-sm text-muted-foreground cursor-pointer">
                Confirmo que tenho 18 anos ou mais e aceito os Termos de Utilização e a Política de Privacidade.
              </Label>
            </div>

            <div className="flex items-start gap-3">
              <Checkbox
                id="marketing"
                checked={formData.marketing_opt_in}
                onCheckedChange={(v) => setFormData({ ...formData, marketing_opt_in: v === true })}
                data-testid="register-marketing-optin"
                className="mt-1"
              />
              <Label htmlFor="marketing" className="text-sm text-muted-foreground cursor-pointer">
                <span className="text-foreground">Quero receber novidades da Intimate.pt</span> (facultativo)
                <span className="block text-xs mt-1">Aceito receber por e-mail novidades sobre contos, séries, conteúdos exclusivos e campanhas promocionais. Posso cancelar a subscrição a qualquer momento.</span>
              </Label>
            </div>

            <Button
              type="submit"
              disabled={loading || !ageConfirmed}
              data-testid="register-submit"
              className="w-full bg-ruby hover:bg-ruby-light text-white rounded-full py-6 font-semibold disabled:opacity-50"
            >
              {loading ? 'A criar conta...' : 'Criar Conta'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
